<script setup lang="ts">
import { AlertIcon, CheckmarkCircleIcon, ImportantIcon, InfoIcon } from '@blpde/nx-icons';
import { computed, useSlots, type Component } from 'vue';
import { hueVars, type NxHue } from '../lib/hue';

export type NxBannerVariant = 'info' | 'warning' | 'danger' | 'success';

const props = withDefaults(
  defineProps<{
    variant: NxBannerVariant;
    /**
     * Vertical alignment of the three slot regions. Defaults to `center`, the
     * shape a one-line hint wants; `start` for long, multi-line text that
     * should keep the icon level with the first line. A banner with a title AND
     * a text below it is multi-line by construction and defaults to `start`.
     */
    align?: 'center' | 'start';
    /** Bold first line above the text; the `#title` slot replaces it. */
    title?: string;
    /**
     * Quiet line at the trailing end of the title row — a date, „in 3
     * Werktagen"; the `#meta` slot replaces it.
     */
    meta?: string;
    /**
     * Category hue instead of the variant's colour — a hint that belongs to a
     * category (an AI hint in violet) rather than to an intent. The variant
     * still picks the icon.
     */
    hue?: NxHue;
    /**
     * Makes the whole banner one button (click, Enter, Space → `click`) — an
     * info card that opens its project. Leave `#buttons` empty then: a button
     * inside a button is not operable.
     */
    clickable?: boolean;
    /**
     * Quiet look: a neutral one-line hint without the icon bubble and the
     * coloured edge — small muted text in a light pill that reads over any
     * content, e.g. a tool hint floating over a canvas. The variant then only
     * names the intent; an own `#icon` stays, plain, without the bubble.
     */
    quiet?: boolean;
    /**
     * When the row gets too narrow for the text and the `#buttons` side by
     * side, the buttons move under the text instead of squeezing it into a
     * column of a few words — the text keeps a readable measure. For a banner
     * across a resizable view (a document editor's notice). Default false.
     */
    wrapButtons?: boolean;
    /**
     * Edge-to-edge strip for a banner that sits against the top or the bottom
     * edge of a panel or view (a notice above a document, a feedback line
     * under it): no rounded corners and no frame on that edge and the sides,
     * only the rule on the side facing the content. Unset: a framed card.
     */
    flush?: 'top' | 'bottom';
  }>(),
  {
    align: undefined,
    title: undefined,
    meta: undefined,
    hue: undefined,
    clickable: false,
    quiet: false,
    wrapButtons: false,
    flush: undefined,
  }
);

const emit = defineEmits<{ click: [event: MouseEvent | KeyboardEvent] }>();

const slots = useSlots();

// The variant already answers the icon question — the same derivation
// `NxToastHost` makes for its kinds (success → the checkmark circle). `danger`
// takes the exclamation mark: `warning` already owns `AlertIcon`, and two
// intents on one glyph would leave colour as the only difference.
const icons = {
  info: InfoIcon,
  warning: AlertIcon,
  danger: ImportantIcon,
  success: CheckmarkCircleIcon,
} as const;

// Nothing maps to nothing on purpose: the icon region hangs off THIS value, so
// a variant without an icon renders no region rather than an empty bubble —
// and that is the seam an opt-out would use, should a surface ever need one.
// A quiet banner is that opt-out: it derives no icon.
const defaultIcon = computed(() =>
  props.quiet ? undefined : (icons[props.variant] as Component | undefined)
);

const hasTitle = computed(() => !!props.title || !!slots.title);
const hasMeta = computed(() => !!props.meta || !!slots.meta);
const hasHead = computed(() => hasTitle.value || hasMeta.value);

const alignment = computed(
  () => props.align ?? (hasHead.value && slots.default ? 'start' : 'center')
);

const hueStyle = computed(() => hueVars(props.hue));

// A control inside the banner (a link in the text) keeps its own click; only a
// click on the banner itself or on plain content is the banner's.
const OWN_CONTROLS = 'a, button, input, select, textarea, [role="button"], [tabindex]';

function onClick(event: MouseEvent) {
  if (!props.clickable) return;
  const control = (event.target as Element | null)?.closest(OWN_CONTROLS);
  if (control && control !== event.currentTarget) return;
  emit('click', event);
}

function onKeydown(event: KeyboardEvent) {
  if (!props.clickable || event.target !== event.currentTarget) return;
  if (event.key !== 'Enter' && event.key !== ' ') return;
  event.preventDefault();
  emit('click', event);
}
</script>

<template>
  <div
    class="banner"
    :class="[
      `variant_${props.variant}`,
      `align_${alignment}`,
      {
        'has-hue': !!props.hue,
        'is-clickable': props.clickable,
        'is-quiet': props.quiet,
        'wrap-buttons': props.wrapButtons,
        'is-flush-top': props.flush === 'top',
        'is-flush-bottom': props.flush === 'bottom',
      },
    ]"
    :style="hueStyle"
    :role="props.clickable ? 'button' : undefined"
    :tabindex="props.clickable ? 0 : undefined"
    @click="onClick"
    @keydown="onKeydown"
  >
    <div v-if="$slots.icon || defaultIcon" class="banner-icon">
      <div>
        <slot name="icon">
          <component :is="defaultIcon" filled class="nxt-icon-sm" aria-hidden="true" />
        </slot>
      </div>
    </div>
    <div class="banner-text">
      <!-- The head row and the body wrapper exist only on a titled banner: an
           untitled one keeps its text a bare run in this region, as before. -->
      <template v-if="hasHead">
        <div class="banner-head">
          <span v-if="hasTitle" class="banner-title">
            <slot name="title">{{ props.title }}</slot>
          </span>
          <span v-if="hasMeta" class="banner-meta">
            <slot name="meta">{{ props.meta }}</slot>
          </span>
        </div>
        <div v-if="$slots.default" class="banner-body"><slot /></div>
      </template>
      <slot v-else />
    </div>
    <div v-if="$slots.buttons" class="banner-buttons">
      <slot name="buttons" />
    </div>
  </div>
</template>

<style scoped>
.variant_info {
  --variant-color: var(--nxt-info-bg);
  --variant-bg: var(--nxt-info-subtle-bg);
}
.variant_warning {
  --variant-color: var(--nxt-warning-bg);
  --variant-bg: var(--nxt-warning-subtle-bg);
}
.variant_danger {
  --variant-color: var(--nxt-danger-bg);
  --variant-bg: var(--nxt-danger-subtle-bg);
}
.variant_success {
  --variant-color: var(--nxt-success-bg);
  --variant-bg: var(--nxt-success-subtle-bg);
}
.banner {
  /* Declared here rather than inherited, so a banner nested in a hue banner
     does not pick up the outer one's border and emphasis. */
  /* A calm frame in a tinted mix and the full tone only as an accent on the
     leading edge: the full-strength frame on every side was loud, in dark mode
     most of all. */
  --variant-border: color-mix(in srgb, var(--variant-color) 35%, var(--nxt-border));
  --variant-emphasis: var(--variant-color);
  display: flex;
  gap: var(--nxt-space-md);
  padding: var(--nxt-space-sm) var(--nxt-space-md);
  border-radius: var(--nxt-radius-md);
  border: var(--nxt-hairline) solid var(--variant-border);
  border-inline-start: calc(var(--nxt-hairline) * 3) solid var(--variant-color);
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
  background-color: var(--variant-bg);
  color: var(--nxt-fg);
}
/* Category hue (NxHue) wins over the variant colours. The border takes the
   hue's own border step, as the tinted badge does, and bold runs its `-fg`
   step, which reads on the tint where the solid fill would not. */
.banner.has-hue {
  --variant-color: var(--hue);
  /* Mixed like the intent tints rather than the hue's own subtle step, which
     is a saturated fill in dark mode — an AI hint must not shout louder than
     a warning. */
  --variant-bg: color-mix(in srgb, var(--hue) 10%, var(--nxt-surface-bg));
  --variant-border: color-mix(in srgb, var(--hue) 35%, var(--nxt-border));
  --variant-emphasis: var(--hue-fg);
}
/* On the container, so the three REGIONS align on the cross axis. Putting
   `display: flex` on the regions instead would turn the text runs into flex
   items and stop them wrapping as one paragraph. */
.align_center {
  align-items: center;
}
.align_start {
  align-items: flex-start;
}
/* Rigid: `min-width: auto` already floors the bubble at its content, so this
   only states the intent — the bubble never squashes. */
.banner-icon {
  flex: 0 0 auto;
  & > div {
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--nxt-radius-full);
    background-color: var(--variant-color);
    color: contrast-color(var(--variant-color));
    padding: var(--nxt-space-xs);
  }
}
.has-hue .banner-icon > div {
  color: var(--hue-on);
}
.banner-text {
  flex: 1 1 0;
}
.banner-text :deep(b) {
  color: var(--variant-emphasis);
}

/* Title row: the meta line sits at the trailing edge on one line and wraps
   under the title only when the row runs out of room. */
.banner-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: var(--nxt-space-sm);
}
.banner-title {
  flex: 1 1 auto;
  min-width: 0;
  color: var(--nxt-fg);
  font-weight: var(--nxt-font-weight-semibold);
}
.banner-meta {
  flex: 0 0 auto;
  margin-inline-start: auto;
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.banner-body {
  margin-block-start: var(--nxt-space-2xs);
}

.is-clickable {
  cursor: pointer;
}
.is-clickable:hover {
  background-color: color-mix(in srgb, var(--variant-color) 10%, var(--variant-bg));
}
.is-clickable:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: var(--nxt-hairline);
}

/* Shrinkable, as it effectively was: the old `flex: 0 0 1` was invalid CSS
   (a unitless third value is no `flex-basis`), so the declaration was dropped
   and the region ran on the initial `0 1 auto`. A slot that wraps its buttons
   relies on that — pinning shrink to 0 would overflow the banner instead. */
.banner-buttons {
  flex: 0 1 auto;
  /* Own row with a token gap: Vue drops the whitespace between two buttons,
     so without it they sat glued together. Wraps when the banner is narrow. */
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--nxt-space-xs);
}
/* wrapButtons: the text claims a readable measure before the buttons get the
   rest; when both no longer fit on one row the buttons wrap under it. */
.banner.wrap-buttons {
  flex-wrap: wrap;
  row-gap: var(--nxt-space-xs);
}
.banner.wrap-buttons .banner-text {
  flex: 1 1 var(--nxt-panel-width-xl);
  min-width: 0;
}

/* flush: a strip across the full width — the frame stays only as the rule
   facing the content (tint and icon carry the variant). */
.banner.is-flush-top,
.banner.is-flush-bottom {
  border-radius: 0;
  border-inline: 0;
}
.banner.is-flush-top {
  border-top: 0;
}
.banner.is-flush-bottom {
  border-bottom: 0;
}

/* Buttons inside read as a quiet outline in the banner's tone. */
.banner-buttons :deep(button:not(#nonExistingIdToBoostSpecificity)) {
  color: var(--variant-emphasis);
  border: var(--nxt-hairline) solid color-mix(in srgb, var(--variant-color) 55%, var(--nxt-border));
  background-color: var(--nxt-surface-bg);

  &:hover:not(:disabled) {
    background-color: color-mix(in srgb, var(--variant-color) 14%, var(--nxt-surface-bg));
    border-color: var(--variant-color);
  }
  &:active:not(:disabled) {
    background-color: color-mix(in srgb, var(--variant-color) 22%, var(--nxt-surface-bg));
    border-color: var(--variant-color);
  }
}

/* Quiet: neutral, no accent edge, no bubble — a hint line that floats over
   content (translucent surface, light shadow) without competing with it. */
.banner.is-quiet {
  --variant-color: var(--nxt-border-strong);
  --variant-bg: color-mix(in srgb, var(--nxt-surface-bg) 92%, transparent);
  --variant-border: var(--nxt-border);
  --variant-emphasis: var(--nxt-fg);
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  border-inline-start-width: var(--nxt-hairline);
  border-inline-start-color: var(--variant-border);
  border-radius: var(--nxt-radius-full);
  box-shadow: var(--nxt-shadow-sm);
  backdrop-filter: blur(calc(var(--nxt-size-unit) * 0.5));
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  text-align: center;
}
.is-quiet .banner-icon > div {
  padding: 0;
  background-color: transparent;
  color: inherit;
}

@media (forced-colors: active) {
  .banner {
    border-color: CanvasText;
  }
}
</style>
